// DS: MO 0.9
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { PapiRequestDaSharedUserCalculateEarnings } from '@wix/da-papi-types';
import SuspenseWithServerFallback from '../../../SuspenseWithServerFallback';
import ModuleLoaderLazy from '../../redux/ModuleLoaderLazy';
import { RequestStatus } from '../../redux/types';
import FallbackErrorBoundary from '../../FallbackErrorBoundary';
import { useEstimatedEarningsDisplay } from '../../hooks';
import { updatePrice } from '../../redux/actions';
import EarningsDisplay from '../../lego/EarningsDisplay';
import ErrorPostfix from '../../lego/ErrorPostfix';
import LoadingPostfix from '../../lego/LoadingPostfix';
import InfoPostfix from '../../lego/InfoPostfix';

export interface Props {
  /**
   * A user inputed price.
   */
  price: number;

  /**
   * Whatever product this calculator should be tied to.
   */
  productType: PapiRequestDaSharedUserCalculateEarnings['product_type'];

  /**
   * Products that are being resold (i.e. Exclusives) need their earnings to be calculated differently.
   */
  isResale?: boolean;

  className?: string;
}

/**
 * Displays estimated earnings given a {@link Props.price|price} and a {@link Props.productType|productType}.
 *
 * This comes with its own redux to fetch the estimated earnings from our backend. You can just plop it into
 * your component.
 */
const EarningsCalculator: React.FC<Props> = ({
  price,
  productType,
  isResale,
  className,
}) => {
  const dispatch = useDispatch();
  const {
    displayId,
    earnings: estimatedEarnings,
    serviceFeePercent,
    requestInfo,
  } = useEstimatedEarningsDisplay(price, productType, isResale);

  const handleRetryBackendRequest = useCallback(() => {
    dispatch(updatePrice(displayId, price));
  }, [dispatch, displayId, price]);

  return (
    // Error boundary for the dynamic redux module
    <FallbackErrorBoundary
      fallback={(_error, clearError) => (
        <EarningsDisplay
          className={className}
          earnings={0.0}
          // clearing the error should make our redux module retry automatically
          postfix={<ErrorPostfix onRetry={clearError} />}
        />
      )}
    >
      <SuspenseWithServerFallback
        fallback={
          <EarningsDisplay
            className={className}
            earnings={estimatedEarnings}
            postfix={<LoadingPostfix />}
          />
        }
      >
        <ModuleLoaderLazy />
        <EarningsDisplay
          className={className}
          earnings={estimatedEarnings}
          postfix={
            <>
              {requestInfo.status === RequestStatus.Error && (
                <ErrorPostfix onRetry={handleRetryBackendRequest} />
              )}
              {requestInfo.status === RequestStatus.Fetching && (
                <LoadingPostfix />
              )}
              {
                // Assuming the backend request succeeded, we should have the service fee percent. Gotta
                // check in this conditional to appease typescript though.
                requestInfo.status === RequestStatus.Idle &&
                  serviceFeePercent !== null &&
                  serviceFeePercent !== undefined && (
                    <InfoPostfix
                      serviceFeePercent={serviceFeePercent}
                      isResale={isResale}
                    />
                  )
              }
            </>
          }
        />
      </SuspenseWithServerFallback>
    </FallbackErrorBoundary>
  );
};

EarningsCalculator.displayName = 'EarningsCalculator';

export default EarningsCalculator;
